import React from "react";
import { Check, AlertCircle } from "lucide-react";
import {
  resolveWorkflowPosition,
  TONES,
} from "../lib/workflows";

/* WorkflowStrip — civic numbered stepper used at the top of any detail
   page that tracks a stateful entity (Project, Consultation, Application,
   Design). One implementation, driven by `/lib/workflows.js` registry.

   Props:
     domain         "project" | "consultation" | "application" | "design"
     current        backend status string (e.g., "In Consultation")
     dateByStage    optional { stageKey: ISO-date } for "as of" timestamps
     compact        smaller spacing for embedded use (default false)
     testId         data-testid root (default `workflow-strip-${domain}`)
*/
export default function WorkflowStrip({
  domain,
  current,
  dateByStage = {},
  compact = false,
  testId,
}) {
  const { stageIdx, branch, isHalted, stages } = resolveWorkflowPosition(
    domain,
    current,
  );
  if (!stages.length) return null;

  // When on a side-branch (e.g., Declined), highlight up to the stage we
  // diverged from so the user can still see "we got here, then halted".
  const haltedAtIdx = branch
    ? stages.findIndex((s) => s.key === branch.afterStage)
    : -1;

  return (
    <div
      className={compact ? "mt-4" : "mt-6"}
      style={{
        border: "1px solid #D9D8E4",
        borderRadius: 4,
        backgroundColor: isHalted ? "#FAF2EE" : "#FBFAFD",
        padding: compact ? "14px 14px 12px" : "20px 16px 16px",
      }}
      data-testid={testId || `workflow-strip-${domain}`}
    >
      <div
        className="flex items-start"
        style={{ gap: 4 }}
      >
        {stages.map((s, i) => {
          const isLastStage = i === stages.length - 1;
          // The terminal happy-path stage (Complete / Approved / Activated)
          // should render as "done" with a checkmark once reached — it's a
          // success state, not an in-progress one.
          const reachedTerminal = !isHalted && isLastStage && i === stageIdx;
          const done = !isHalted
            ? i < stageIdx || reachedTerminal
            : i <= haltedAtIdx;
          const current = !isHalted && i === stageIdx && !reachedTerminal;
          const at = dateByStage[s.key];
          const date = at
            ? new Date(at).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              })
            : null;
          const isLast = isLastStage;
          return (
            <React.Fragment key={s.key}>
              <div
                className="flex flex-col items-center text-center"
                style={{ flex: 1, minWidth: 0 }}
                data-testid={`workflow-stage-${domain}-${i}`}
              >
                <span
                  className="tabular-nums"
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 999,
                    background: current
                      ? "#1B1F4B"
                      : done
                        ? "#5B6196"
                        : "#FBFAFD",
                    border:
                      "1px solid " +
                      (current || done ? "transparent" : "#D9D8E4"),
                    color: current || done ? "#FBFAFD" : "#6C6B85",
                    fontSize: 11,
                    fontWeight: 700,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  aria-current={current ? "step" : undefined}
                >
                  {done ? <Check size={13} strokeWidth={2.4} /> : i + 1}
                </span>
                <span
                  style={{
                    fontSize: 11,
                    color: current
                      ? "#1B1F4B"
                      : done
                        ? "#272636"
                        : "#6C6B85",
                    marginTop: 6,
                    fontWeight: current ? 700 : 500,
                    fontFamily: '"Nunito Sans", sans-serif',
                    letterSpacing: "0.01em",
                    lineHeight: 1.25,
                  }}
                >
                  {s.label}
                </span>
                {date && (done || current) && (
                  <span
                    className="tabular-nums"
                    style={{
                      marginTop: 2,
                      fontSize: 10.5,
                      color: "#6C6B85",
                      fontFamily: '"Newsreader", Georgia, serif',
                      fontStyle: "italic",
                      lineHeight: 1.2,
                    }}
                  >
                    {current ? `entered ${date}` : date}
                  </span>
                )}
              </div>
              {!isLast && (
                <span
                  aria-hidden
                  style={{
                    flex: "0 0 auto",
                    alignSelf: "flex-start",
                    marginTop: 13,
                    width: 18,
                    height: 1,
                    background:
                      (!isHalted && i < stageIdx) || (isHalted && i < haltedAtIdx)
                        ? "#5B6196"
                        : "#D9D8E4",
                  }}
                />
              )}
            </React.Fragment>
          );
        })}

        {/* Side-branch chip (Declined / Rejected / More info requested) */}
        {branch && (
          <SideBranchChip branch={branch} domain={domain} />
        )}
      </div>
    </div>
  );
}

function SideBranchChip({ branch, domain }) {
  const tone = TONES[branch.tone] || TONES.slate;
  return (
    <div
      className="flex flex-col items-center text-center"
      style={{ flex: "0 0 auto", paddingLeft: 14, minWidth: 0 }}
      data-testid={`workflow-branch-${domain}`}
    >
      <span
        style={{
          minWidth: 26,
          height: 26,
          padding: "0 10px",
          borderRadius: 999,
          background: tone.bg,
          color: tone.fg,
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 5,
          whiteSpace: "nowrap",
        }}
      >
        <AlertCircle size={12} strokeWidth={2.2} />
        Halted
      </span>
      <span
        style={{
          fontSize: 11,
          color: "#272636",
          marginTop: 6,
          fontWeight: 700,
          fontFamily: '"Nunito Sans", sans-serif',
          letterSpacing: "0.01em",
          lineHeight: 1.25,
          maxWidth: 130,
        }}
      >
        {branch.label}
      </span>
    </div>
  );
}
